import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React, { forwardRef, useState, useRef } from 'react';
import propTypes from '../../../node_modules/prop-types/index.js';
import Icon from '../Icon/Icon.js';
import Progress from '../Progress/Progress.js';
import { uniqueId, safeRest, noop, rem } from '../../utils/functions.js';
import handle from '../../utils/handle.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import { BUTTON_VARIANT, TEXT_HEIGHT } from './constants.js';
import { ButtonWrapper, IconWrapper, SpinnerWrapper } from './styledComponents.js';
import { getTextColor } from './utils.js';
import useDerivedState from '../../hooks/useDerivedState.js';
import useTheme from '../../hooks/useTheme.js';

var _excluded = ["id", "onClick", "ariaLabel", "variant", "children", "icon", "hoverIcon", "saving", "disabled", "customStyle", "active"];
var getDefaultTertiaryIcon = function getDefaultTertiaryIcon(isHovered, isActive, isDisabled, isBack, theme) {
  var chevron = isBack ? 'chevronleft' : 'chevronright';

  if (isDisabled) {
    return /*#__PURE__*/React.createElement(Icon, {
      manulifeName: "".concat(chevron, "_withcircle_filled"),
      fill: theme.colorPalette.neutralColors.light.shade_white,
      secondaryColor: theme.colorPalette.neutralColors.light.shade_dark_3,
      width: rem(TEXT_HEIGHT),
      height: rem(TEXT_HEIGHT)
    });
  }

  if (isActive) {
    return /*#__PURE__*/React.createElement(Icon, {
      manulifeName: "".concat(chevron, "_withcircle_filled"),
      fill: theme.colorPalette.neutralColors.light.shade_white,
      secondaryColor: theme.colorPalette.accentColor.shade_dark_3,
      width: rem(TEXT_HEIGHT),
      height: rem(TEXT_HEIGHT)
    });
  }

  if (isHovered) {
    return /*#__PURE__*/React.createElement(Icon, {
      manulifeName: "".concat(chevron, "_withcircle_filled"),
      fill: theme.colorPalette.neutralColors.light.shade_white,
      secondaryColor: theme.colorPalette.accentColor.shade_dark_2,
      width: rem(TEXT_HEIGHT),
      height: rem(TEXT_HEIGHT)
    });
  }

  return /*#__PURE__*/React.createElement(Icon, {
    manulifeName: "".concat(chevron, "_withcircle"),
    fill: theme.colorPalette.accentColor.shade_default,
    secondaryColor: theme.colorPalette.accentColor.shade_default,
    width: rem(TEXT_HEIGHT),
    height: rem(TEXT_HEIGHT)
  });
};

var getButtonIcon = function getButtonIcon(isHovered, active, disabled, hoverIcon, icon, theme, variant) {
  var ButtonIcon = isHovered && !disabled ? hoverIcon : icon;

  if (!ButtonIcon && (variant === BUTTON_VARIANT.TERTIARY || variant === BUTTON_VARIANT.TERTIARY_BACK)) {
    ButtonIcon = getDefaultTertiaryIcon(isHovered, active, disabled, variant === BUTTON_VARIANT.TERTIARY_BACK, theme);
  } else if (variant === BUTTON_VARIANT.TERTIARY_NOICON) {
    ButtonIcon = null;
  } else ;

  return ButtonIcon;
};

var Button = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var controlledId = _ref.id,
      onClick = _ref.onClick,
      ariaLabel = _ref.ariaLabel,
      variant = _ref.variant,
      children = _ref.children,
      icon = _ref.icon,
      _ref$hoverIcon = _ref.hoverIcon,
      hoverIcon = _ref$hoverIcon === void 0 ? icon : _ref$hoverIcon,
      saving = _ref.saving,
      disabled = _ref.disabled,
      customStyle = _ref.customStyle,
      active = _ref.active,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _useDerivedState = useDerivedState(uniqueId('button-id-'), controlledId),
      _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
      id = _useDerivedState2[0];

  var theme = useTheme();
  var safeProps = safeRest(rest);

  var _safeCustomStyle = safeCustomStyle(customStyle),
      buttonStyle = _safeCustomStyle.buttonStyle;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isHovered = _useState2[0],
      setIsHovered = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isActive = _useState4[0],
      setIsActive = _useState4[1];

  var buttonRef = useRef(null);
  var refDom = ref || buttonRef; // ref to keep track of reset ripple timeout

  var ButtonIcon = getButtonIcon(isHovered, active || isActive, disabled, hoverIcon, icon, theme, variant); // build the Saving Spinner if required

  var progressProps = {
    size: 20,
    magnitude: 7,
    isFloating: false,
    color: getTextColor(variant, isHovered, active, disabled, theme)
  };
  var Spinner = /*#__PURE__*/React.createElement(SpinnerWrapper, {
    "data-testid": "".concat(id, "-saving-test")
  }, /*#__PURE__*/React.createElement(Progress, progressProps));
  return /*#__PURE__*/React.createElement(ButtonWrapper, _extends({}, safeProps, {
    id: id,
    variant: variant,
    onClick: handle(onClick),
    onMouseDown: function onMouseDown() {
      setIsActive(true);
    },
    onMouseUp: function onMouseUp() {
      setIsActive(false);
    },
    onMouseOver: function onMouseOver() {
      return setIsHovered(true);
    },
    onMouseOut: function onMouseOut() {
      setIsHovered(false);
      setIsActive(false);
    },
    onFocus: function onFocus() {
      return setIsHovered(true);
    },
    onBlur: function onBlur() {
      return setIsHovered(false);
    },
    "aria-label": ariaLabel,
    buttonStyle: buttonStyle,
    ref: refDom,
    disabled: disabled,
    hover: isHovered,
    saving: saving,
    active: active || isActive,
    theme: theme
  }), ButtonIcon && /*#__PURE__*/React.createElement(IconWrapper, {
    disabled: disabled,
    active: active || isActive,
    hovered: isHovered,
    variant: variant,
    theme: theme
  }, ButtonIcon), children, saving && Spinner);
});
process.env.NODE_ENV !== "production" ? Button.propTypes = {
  /** ID is required for Automated Testing, Google analytics and Google Tag Manager */
  id: propTypes.string,

  /** Aria-label for button */
  ariaLabel: propTypes.string,

  /** Icon to add to the button */
  icon: propTypes.node,

  /** Hover Icon to add to the button */
  hoverIcon: propTypes.node,

  /** Indicates button should show "saving" spinner */
  saving: propTypes.bool,

  /** active is bool */
  active: propTypes.bool,

  /** flag to disable button */
  disabled: propTypes.bool,

  /** Click handler for button */
  onClick: propTypes.func,

  /** Content of the button */
  children: propTypes.node.isRequired,

  /** Style overrides */
  customStyle: propTypes.shape({
    buttonStyle: propTypes.object
  }),

  /** Type of button */
  variant: propTypes.oneOf([BUTTON_VARIANT.PRIMARY, BUTTON_VARIANT.SECONDARY, BUTTON_VARIANT.TERTIARY, BUTTON_VARIANT.TERTIARY_BACK, BUTTON_VARIANT.TERTIARY_NOICON, BUTTON_VARIANT.SIGN_IN])
} : void 0;
Button.defaultProps = {
  id: undefined,
  ariaLabel: undefined,
  icon: undefined,
  hoverIcon: undefined,
  saving: false,
  active: false,
  disabled: false,
  onClick: noop,
  variant: BUTTON_VARIANT.PRIMARY,
  customStyle: {
    buttonStyle: {}
  }
};
process.env.NODE_ENV !== "production" ? Button.displayName = 'Button' : void 0;

export default Button;
export { getDefaultTertiaryIcon };
